/**
 * Theme mode provider — holds light/dark in localStorage and renders MUI's ThemeProvider
 * + CssBaseline.
 *
 * NOT MOUNTED YET. A later step wraps App.jsx in it:
 *
 *   import ThemeModeProvider from 'theme/ThemeModeProvider'
 *   <ThemeModeProvider><App /></ThemeModeProvider>
 *
 * Light is the default. The OS `prefers-color-scheme` is deliberately NOT consulted: this
 * is an internal desk tool with a light-first visual direction, and a stored explicit
 * choice is the only thing that should flip it.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import { createAppTheme, themes } from './index'
import { DEFAULT_MODE, MODES } from './tokens'

export const THEME_MODE_STORAGE_KEY = 'themeMode'

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY)

    return MODES.includes(stored) ? stored : DEFAULT_MODE
  } catch (e) {
    // private mode / storage disabled — fall back to the default rather than crash
    return DEFAULT_MODE
  }
}

const writeStoredMode = (mode) => {
  try {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
  } catch (e) {
    // non-fatal: the session still works, it just will not persist
  }
}

const ThemeModeContext = createContext({
  mode: DEFAULT_MODE,
  isDark: false,
  setMode: () => {},
  toggleMode: () => {},
})

/** Read and control the active mode from anywhere under the provider. */
export const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({ children, initialMode }) => {
  const [mode, setModeState] = useState(() => (MODES.includes(initialMode) ? initialMode : readStoredMode()))

  const setMode = useCallback((next) => {
    const resolved = MODES.includes(next) ? next : DEFAULT_MODE

    setModeState(resolved)
    writeStoredMode(resolved)
  }, [])

  const toggleMode = useCallback(() => {
    setModeState((current) => {
      const next = current === 'dark' ? 'light' : 'dark'

      writeStoredMode(next)

      return next
    })
  }, [])

  useEffect(() => {
    // Lets CSS modules and any non-MUI markup hook the mode with
    // `:root[data-theme="dark"] { … }` while the migration is in progress.
    const root = document.documentElement

    root.dataset.theme = mode
    root.style.colorScheme = mode
  }, [mode])

  // themes.light / themes.dark are prebuilt; createAppTheme is the escape hatch if a
  // consumer ever passes a mode that was not prebuilt.
  const theme = useMemo(() => themes[mode] || createAppTheme(mode), [mode])

  const value = useMemo(() => ({ mode, isDark: mode === 'dark', setMode, toggleMode }), [mode, setMode, toggleMode])

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export { ThemeModeContext }

export default ThemeModeProvider
